import React from "react";
import { Star, Quote } from "lucide-react";
import Image from "next/image";
import { z } from "zod";

const TestimonialSchema = z.object({
	name: z.string(),
	title: z.string(),
	location: z.string(),
	image: z.string(),
	quote: z.string(),
	result: z.string(),
});

const StatSchema = z.object({
	value: z.string(),
	label: z.string(),
});

const TestimonialsSchema = z.object({
	badgeText: z.string().default("Success Stories"),
	header: z.string().default("What Founders Are Saying"),
	highlightText: z.string().default("About Our Community"),
	description: z
		.string()
		.default(
			"Real stories from PropTech founders who've leveraged our community to scale their businesses and achieve breakthrough results."
		),
	testimonials: z.array(TestimonialSchema).default([
		{
			name: "Alex Thompson",
			title: "CEO, RentTech Pro",
			location: "Toronto, Canada",
			image:
				"https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400",
			quote:
				"PropTechBuzz isn't just a network—it's a catalyst. The connections I've made here led to a $3M Series A and partnerships that transformed our business.",
			result: "Raised $3M Series A",
		},
		{
			name: "Sofia Martinez",
			title: "Founder, SmartProperty AI",
			location: "Barcelona, Spain",
			image:
				"https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=400",
			quote:
				"The quality of conversations here is unmatched. Every interaction is with someone who truly understands PropTech challenges and opportunities.",
			result: "3 Key Partnerships",
		},
		{
			name: "Michael Chang",
			title: "Co-founder, BuildFlow",
			location: "Hong Kong",
			image:
				"https://images.pexels.com/photos/1043474/pexels-photo-1043474.jpeg?auto=compress&cs=tinysrgb&w=400",
			quote:
				"As an early member, I've watched this community grow into the most valuable PropTech network globally. The early access to tools and events has been incredible.",
			result: "Featured in TechCrunch",
		},
	]),
	bottomSectionTitle: z.string().default("Join 500+ Successful PropTech Founders"),
	bottomSectionDescription: z
		.string()
		.default(
			"Don't just take our word for it. Experience the difference a truly focused PropTech community can make for your startup."
		),
	stats: z.array(StatSchema).default([
		{ value: "98%", label: "Satisfaction Rate" },
		{ value: "$2.5B+", label: "Total Funding Raised" },
		{ value: "250+", label: "Partnerships Formed" },
		{ value: "85%", label: "Active Weekly" },
	]),
	buttonText: z.string().default("Start Your Success Story"),
});

type TestimonialsProps = z.input<typeof TestimonialsSchema>;

const Testimonials: React.FC<TestimonialsProps> = (props = {}) => {
	const data = TestimonialsSchema.parse(props);

	return (
		<section className="bg-white py-24">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-16 text-center">
					<span className="mb-4 inline-flex items-center rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-800">
						<Star className="mr-2 h-4 w-4" />
						{data.badgeText}
					</span>
					<h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-5xl">
						{data.header}
						<span className="block bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
							{data.highlightText}
						</span>
					</h2>
					<p className="mx-auto max-w-3xl text-xl leading-relaxed text-gray-600">
						{data.description}
					</p>
				</div>

				<div className="mb-16 grid gap-8 lg:grid-cols-3">
					{data.testimonials.map((testimonial, index) => (
						<div
							key={index}
							className="relative rounded-2xl border border-transparent bg-gray-50 p-8 transition-all duration-300 hover:-translate-y-2 hover:border-gray-200 hover:bg-white hover:shadow-xl"
						>
							<div className="absolute right-6 top-6">
								<Quote className="h-8 w-8 text-gray-300" />
							</div>

							<div className="mb-6 flex items-center">
								<Image
									src={testimonial.image}
									alt={testimonial.name}
									className="mr-4 h-16 w-16 rounded-full object-cover"
									width={64}
									height={64}
								/>
								<div>
									<h4 className="font-bold text-gray-900">{testimonial.name}</h4>
									<p className="text-gray-600">{testimonial.title}</p>
									<p className="text-sm text-gray-500">{testimonial.location}</p>
								</div>
							</div>

							<div className="mb-4 flex">
								{[...Array(5)].map((_, i) => (
									<Star key={i} className="h-5 w-5 fill-current text-yellow-400" />
								))}
							</div>

							<blockquote className="mb-6 text-lg leading-relaxed text-gray-700">
								&quot;{testimonial.quote}&quot;
							</blockquote>

							<div className="inline-block rounded-full bg-gradient-to-r from-blue-500 to-teal-500 px-4 py-2 text-sm font-semibold text-white">
								{testimonial.result}
							</div>
						</div>
					))}
				</div>

				<div className="rounded-3xl bg-gradient-to-r from-green-600 to-blue-600 p-8 text-center text-white md:p-12">
					<h3 className="mb-6 text-2xl font-bold md:text-3xl">{data.bottomSectionTitle}</h3>
					<p className="mx-auto mb-8 max-w-2xl text-lg text-green-100">
						{data.bottomSectionDescription}
					</p>

					<div className="mb-8 grid gap-8 md:grid-cols-4">
						{data.stats.map((stat, index) => (
							<div key={index}>
								<div className="mb-2 text-3xl font-bold text-green-300">{stat.value}</div>
								<div className="text-green-100">{stat.label}</div>
							</div>
						))}
					</div>

					<button className="rounded-full bg-orange-600 px-8 py-4 text-lg font-semibold text-white transition-all duration-200 hover:bg-orange-700 hover:shadow-lg">
						{data.buttonText}
					</button>
				</div>
			</div>
		</section>
	);
};

export default Testimonials;
